import React, { useEffect, useMemo, useState } from "react";

import "./MobileFilterPanel.css";

import { PRESET_RANGES, SORT_OPTIONS } from "./shopFilterOptions";
import { findPresetRange, normalizeList } from "./shopFilterHelpers";

const FILTER_FACETS = [
  { key: "colors", label: "Color" },
  { key: "sizes", label: "Size" },
  { key: "materials", label: "Material" },
  { key: "patterns", label: "Pattern" },
];

const createDraft = (filters = {}) => ({
  sort: filters.sort || "newest",
  minPrice: filters.minPrice || "",
  maxPrice: filters.maxPrice || "",
  inStock: Boolean(filters.inStock),

  colors: Array.isArray(filters.colors) ? [...filters.colors] : [],
  sizes: Array.isArray(filters.sizes) ? [...filters.sizes] : [],
  materials: Array.isArray(filters.materials) ? [...filters.materials] : [],
  patterns: Array.isArray(filters.patterns) ? [...filters.patterns] : [],
});

const Chevron = ({ open = false }) => (
  <span className={`mobile-filter-panel__chevron ${open ? "is-open" : ""}`}>
    <svg viewBox="0 0 12 8" aria-hidden="true">
      <path
        d="M1 1.5L6 6.5L11 1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const CheckIcon = () => (
  <span className="mobile-filter-panel__checkmark" aria-hidden="true">
    <svg viewBox="0 0 16 16">
      <path
        d="M3.25 8.25L6.5 11.5L12.75 4.75"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

function formatPriceSummary(minPrice, maxPrice) {
  const preset = findPresetRange(minPrice, maxPrice, PRESET_RANGES);

  if (preset) return preset.label;
  if (minPrice || maxPrice) return `£${minPrice || "0"} – £${maxPrice || "∞"}`;
  return "Any price";
}

function formatFacetSummary(selected = [], totalOptions = 0) {
  if (!selected.length) return `${totalOptions} options`;
  if (selected.length === 1) return selected[0];
  if (selected.length === 2) return `${selected[0]}, ${selected[1]}`;
  return `${selected[0]}, ${selected[1]} +${selected.length - 2}`;
}

function normalizePriceValue(value) {
  const text = String(value ?? "").trim();
  if (!text) return "";

  const parsed = Number(text);
  if (!Number.isFinite(parsed) || parsed < 0) return "";

  return String(parsed);
}

function getFacetItems(facets, draft, key) {
  return normalizeList([...(facets?.[key] || []), ...(draft?.[key] || [])]);
}

function hasFacetValues(facets, draft, key) {
  return getFacetItems(facets, draft, key).length > 0;
}

export default function MobileFilterPanel({
  filters,
  facets,
  onApply,
  onClearAll,
  onClose,
}) {
  const [draft, setDraft] = useState(createDraft(filters));
  const [showCustomPrice, setShowCustomPrice] = useState(false);

  const [openSections, setOpenSections] = useState({
    colors: false,
    sizes: false,
    materials: false,
    patterns: false,
    price: false,
    inStock: true,
    sort: false,
  });

  useEffect(() => {
    setDraft(createDraft(filters));
  }, [filters]);

  useEffect(() => {
    setOpenSections((prev) => {
      const next = { ...prev };

      FILTER_FACETS.forEach(({ key }) => {
        if ((filters[key] || []).length > 0) {
          next[key] = true;
        }
      });

      if (filters.minPrice || filters.maxPrice) {
        next.price = true;
        setShowCustomPrice(true);
      }

      if (filters.sort && filters.sort !== "newest") {
        next.sort = true;
      }

      return next;
    });
  }, [filters]);

  const visibleFacets = useMemo(
    () => FILTER_FACETS.filter(({ key }) => hasFacetValues(facets, draft, key)),
    [facets, draft],
  );

  const selectedSort =
    SORT_OPTIONS.find((option) => option.value === draft.sort) ||
    SORT_OPTIONS[0];

  const selectedPricePreset = findPresetRange(
    draft.minPrice,
    draft.maxPrice,
    PRESET_RANGES,
  );

  const activeCount = useMemo(() => {
    let total = 0;

    FILTER_FACETS.forEach(({ key }) => {
      total += draft[key]?.length || 0;
    });

    if (draft.minPrice || draft.maxPrice) total += 1;
    if (draft.inStock) total += 1;
    if (draft.sort && draft.sort !== "newest") total += 1;

    return total;
  }, [draft]);

  const activePills = useMemo(() => {
    const pills = [];

    FILTER_FACETS.forEach(({ key, label }) => {
      (draft[key] || []).forEach((value) => {
        pills.push({
          key: `${key}-${value}`,
          label: `${label}: ${value}`,
          onRemove: () =>
            setDraft((prev) => ({
              ...prev,
              [key]: (prev[key] || []).filter((item) => item !== value),
            })),
        });
      });
    });

    if (draft.minPrice || draft.maxPrice) {
      pills.push({
        key: "price",
        label: `Price: ${formatPriceSummary(draft.minPrice, draft.maxPrice)}`,
        onRemove: () =>
          setDraft((prev) => ({
            ...prev,
            minPrice: "",
            maxPrice: "",
          })),
      });
    }

    if (draft.inStock) {
      pills.push({
        key: "inStock",
        label: "In stock",
        onRemove: () => setDraft((prev) => ({ ...prev, inStock: false })),
      });
    }

    if (draft.sort && draft.sort !== "newest") {
      pills.push({
        key: "sort",
        label: selectedSort.label,
        onRemove: () => setDraft((prev) => ({ ...prev, sort: "newest" })),
      });
    }

    return pills;
  }, [draft, selectedSort.label]);

  const updateField = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const toggleFacetValue = (facetKey, value) => {
    setDraft((prev) => {
      const current = prev[facetKey] || [];
      const exists = current.includes(value);

      return {
        ...prev,
        [facetKey]: exists
          ? current.filter((item) => item !== value)
          : [...current, value],
      };
    });
  };

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const clearFacet = (facetKey) => {
    setDraft((prev) => ({ ...prev, [facetKey]: [] }));
  };

  const clearPrice = () => {
    setDraft((prev) => ({
      ...prev,
      minPrice: "",
      maxPrice: "",
    }));
    setShowCustomPrice(false);
  };

  const handleReset = () => {
    const emptyDraft = createDraft();

    setDraft(emptyDraft);
    setShowCustomPrice(false);

    setOpenSections({
      colors: false,
      sizes: false,
      materials: false,
      patterns: false,
      price: false,
      inStock: true,
      sort: false,
    });

    if (typeof onClearAll === "function") {
      onClearAll();
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    let nextMinPrice = normalizePriceValue(draft.minPrice);
    let nextMaxPrice = normalizePriceValue(draft.maxPrice);

    if (
      nextMinPrice &&
      nextMaxPrice &&
      Number(nextMinPrice) > Number(nextMaxPrice)
    ) {
      [nextMinPrice, nextMaxPrice] = [nextMaxPrice, nextMinPrice];
    }

    onApply({
      colors: draft.colors,
      sizes: draft.sizes,
      materials: draft.materials,
      patterns: draft.patterns,

      minPrice: nextMinPrice,
      maxPrice: nextMaxPrice,
      inStock: draft.inStock,
      sort: draft.sort || "newest",

      brands: [],
      unitTypes: [],
      finishes: [],
      compatibilities: [],
    });
  };

  const renderFacetSection = ({ key, label }) => {
    const items = getFacetItems(facets, draft, key);
    const selectedValues = draft[key] || [];
    const selectedCount = selectedValues.length;
    const isOpen = Boolean(openSections[key]);

    if (!items.length && !selectedCount) return null;

    return (
      <section key={key} className="mobile-filter-panel__section">
        <button
          type="button"
          className="mobile-filter-panel__section-head"
          onClick={() => toggleSection(key)}
          aria-expanded={isOpen}
        >
          <div className="mobile-filter-panel__section-copy">
            <h3 className="mobile-filter-panel__section-title">{label}</h3>
            <p className="mobile-filter-panel__section-meta">
              {formatFacetSummary(selectedValues, items.length)}
            </p>
          </div>

          <div className="mobile-filter-panel__section-actions">
            {selectedCount > 0 ? (
              <span
                className="mobile-filter-panel__section-count"
                aria-hidden="true"
              >
                {selectedCount}
              </span>
            ) : null}

            <Chevron open={isOpen} />
          </div>
        </button>

        {isOpen ? (
          <div className="mobile-filter-panel__section-body">
            {selectedCount > 0 ? (
              <div className="mobile-filter-panel__section-tools">
                <button
                  type="button"
                  className="mobile-filter-panel__mini-clear"
                  onClick={() => clearFacet(key)}
                >
                  Clear {label.toLowerCase()}
                </button>
              </div>
            ) : null}

            <div className="mobile-filter-panel__chips">
              {items.map((item) => {
                const active = selectedValues.includes(item);

                return (
                  <button
                    key={item}
                    type="button"
                    className={`mobile-filter-panel__chip ${
                      active ? "is-active" : ""
                    }`}
                    onClick={() => toggleFacetValue(key, item)}
                  >
                    {item}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </section>
    );
  };

  const renderPriceSection = () => {
    const isOpen = Boolean(openSections.price);
    const hasPrice = Boolean(draft.minPrice || draft.maxPrice);

    return (
      <section className="mobile-filter-panel__section">
        <button
          type="button"
          className="mobile-filter-panel__section-head"
          onClick={() => toggleSection("price")}
          aria-expanded={isOpen}
        >
          <div className="mobile-filter-panel__section-copy">
            <h3 className="mobile-filter-panel__section-title">Price</h3>
            <p className="mobile-filter-panel__section-meta">
              {formatPriceSummary(draft.minPrice, draft.maxPrice)}
            </p>
          </div>

          <div className="mobile-filter-panel__section-actions">
            {hasPrice ? (
              <span
                className="mobile-filter-panel__section-count"
                aria-hidden="true"
              >
                1
              </span>
            ) : null}

            <Chevron open={isOpen} />
          </div>
        </button>

        {isOpen ? (
          <div className="mobile-filter-panel__section-body">
            {hasPrice ? (
              <div className="mobile-filter-panel__section-tools">
                <button
                  type="button"
                  className="mobile-filter-panel__mini-clear"
                  onClick={clearPrice}
                >
                  Clear price
                </button>
              </div>
            ) : null}

            <div className="mobile-filter-panel__chips">
              {PRESET_RANGES.map((range) => {
                const active =
                  selectedPricePreset?.min === range.min &&
                  selectedPricePreset?.max === range.max;

                return (
                  <button
                    key={range.label}
                    type="button"
                    className={`mobile-filter-panel__chip ${
                      active ? "is-active" : ""
                    }`}
                    onClick={() => {
                      updateField("minPrice", String(range.min));
                      updateField(
                        "maxPrice",
                        range.max == null ? "" : String(range.max),
                      );
                      setShowCustomPrice(false);
                    }}
                  >
                    {range.label}
                  </button>
                );
              })}
            </div>

            <div className="mobile-filter-panel__custom-price">
              <button
                type="button"
                className={`mobile-filter-panel__custom-trigger ${
                  showCustomPrice ? "is-open" : ""
                }`}
                onClick={() => setShowCustomPrice((prev) => !prev)}
              >
                <span>Custom range</span>
                <Chevron open={showCustomPrice} />
              </button>

              {showCustomPrice ? (
                <div className="mobile-filter-panel__price-grid">
                  <div>
                    <label
                      className="mobile-filter-panel__label"
                      htmlFor="mobile-min-price"
                    >
                      Min price
                    </label>

                    <input
                      id="mobile-min-price"
                      type="number"
                      min="0"
                      inputMode="decimal"
                      value={draft.minPrice}
                      onChange={(event) =>
                        updateField("minPrice", event.target.value)
                      }
                      className="mobile-filter-panel__input"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label
                      className="mobile-filter-panel__label"
                      htmlFor="mobile-max-price"
                    >
                      Max price
                    </label>

                    <input
                      id="mobile-max-price"
                      type="number"
                      min="0"
                      inputMode="decimal"
                      value={draft.maxPrice}
                      onChange={(event) =>
                        updateField("maxPrice", event.target.value)
                      }
                      className="mobile-filter-panel__input"
                      placeholder="500"
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>
    );
  };

  const renderAvailabilitySection = () => (
    <section className="mobile-filter-panel__availability">
      <div>
        <h3 className="mobile-filter-panel__section-title">In stock</h3>
        <p className="mobile-filter-panel__section-meta">
          {draft.inStock ? "In stock only" : "Show all availability"}
        </p>
      </div>

      <button
        type="button"
        role="switch"
        aria-checked={draft.inStock}
        className={`mobile-filter-panel__switch ${
          draft.inStock ? "is-on" : ""
        }`}
        onClick={() => updateField("inStock", !draft.inStock)}
      >
        <span className="mobile-filter-panel__switch-knob" />
      </button>
    </section>
  );

  const renderSortSection = () => {
    const isOpen = Boolean(openSections.sort);

    return (
      <section className="mobile-filter-panel__section">
        <button
          type="button"
          className="mobile-filter-panel__section-head"
          onClick={() => toggleSection("sort")}
          aria-expanded={isOpen}
        >
          <div className="mobile-filter-panel__section-copy">
            <h3 className="mobile-filter-panel__section-title">Sort</h3>
            <p className="mobile-filter-panel__section-meta">
              {selectedSort.label}
            </p>
          </div>

          <div className="mobile-filter-panel__section-actions">
            {draft.sort && draft.sort !== "newest" ? (
              <span
                className="mobile-filter-panel__section-count"
                aria-hidden="true"
              >
                1
              </span>
            ) : null}

            <Chevron open={isOpen} />
          </div>
        </button>

        {isOpen ? (
          <div className="mobile-filter-panel__option-list">
            {SORT_OPTIONS.map((option) => {
              const active = selectedSort.value === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  className={`mobile-filter-panel__option-row ${
                    active ? "is-active" : ""
                  }`}
                  onClick={() => updateField("sort", option.value)}
                >
                  <span>{option.label}</span>
                  {active ? <CheckIcon /> : null}
                </button>
              );
            })}
          </div>
        ) : null}
      </section>
    );
  };

  return (
    <form className="mobile-filter-panel" onSubmit={handleSubmit}>
      <div className="mobile-filter-panel__top">
        <div>
          <p className="mobile-filter-panel__eyebrow">Refine selection</p>
          <h2 className="mobile-filter-panel__title">Filters</h2>
        </div>

        <div className="mobile-filter-panel__top-actions">
          {activeCount > 0 ? (
            <button
              type="button"
              className="mobile-filter-panel__clear"
              onClick={handleReset}
            >
              Clear all
            </button>
          ) : null}

          {typeof onClose === "function" ? (
            <button
              type="button"
              className="mobile-filter-panel__close"
              onClick={onClose}
              aria-label="Close filters"
            >
              ×
            </button>
          ) : null}
        </div>
      </div>

      {activePills.length > 0 ? (
        <div className="mobile-filter-panel__active">
          {activePills.map((item) => (
            <button
              key={item.key}
              type="button"
              className="mobile-filter-panel__active-chip"
              onClick={item.onRemove}
            >
              <span>{item.label}</span>
              <span aria-hidden="true">×</span>
            </button>
          ))}
        </div>
      ) : null}

      {visibleFacets.map(renderFacetSection)}
      {renderPriceSection()}
      {renderAvailabilitySection()}
      {renderSortSection()}

      <div className="mobile-filter-panel__footer">
        <button
          type="button"
          onClick={handleReset}
          className="mobile-filter-panel__footer-btn mobile-filter-panel__footer-btn--ghost"
        >
          Reset
        </button>

        <button
          type="submit"
          className="mobile-filter-panel__footer-btn mobile-filter-panel__footer-btn--solid"
        >
          Apply{activeCount > 0 ? ` (${activeCount})` : ""}
        </button>
      </div>
    </form>
  );
}
